import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import DatePicker from 'components/Datepicker';
import FiltersModal from 'components/FiltersModal';
import CategoryIcon from 'components/CategoryIcon';
import ScrollingPlaceholder from 'components/ScrollingPlaceholder';

import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  MapPinIcon,
  XMarkIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';

import { searchEvents } from '../../redux/actions/events';
import { searchBrands } from '../../redux/actions/brands';
import { fetchSuggestions } from '../../redux/actions/suggestions';

import { useMediaQuery } from 'hooks/useMediaQuery';
import { categories } from '../../utils/categories';
import getCategorySuggestions, {
  getIdsFromQuery,
} from '../../utils/searchSuggestions';
import { UTILITIES } from '../../utils/utilities';

const EMPTY_LOCATION = {
  option: null,
  label: '',
  primary: '',
  city: '',
  country: '',
  countryCode: '',
  placeId: '',
};

const EventSearchBar = ({
  currentSearch,
  hasLabel,
  isAuthenticated,
  redirectToListings = false,
  listingsPath = '/listings',
  initialState = null,
  isHomepage = false,
  onEventFiltersChange,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const wrapperRef = useRef(null);

  const justSelectedSuggestion = useRef(false);

  const [query, setQuery] = useState('');
  const [location, setLocation] = useState(EMPTY_LOCATION);
  const [selectedDates, setSelectedDates] = useState({ from: '', to: '' });
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const [selectedFilters, setSelectedFilters] = useState({
    macroCategories: [],
    microCategories: [],
    utilities: [],
    maxPrice: null,
    hidePastEvents: true,
    hideLiveEvents: false,
    monthRange: { from: '', to: '' },
  });

  const skipNextAutoSearchRef = useRef(false);
  const didSearchOnceRef = useRef(false);

  const isMobile = useMediaQuery('(max-width: 640px)');
  const isTabletOrBelow = useMediaQuery('(max-width: 800px)'); // ✅ breakpoint

  const initialStateKey = useMemo(
    () => JSON.stringify(initialState || null),
    [initialState]
  );

  const isYearMonth = (v) =>
    typeof v === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

  const toYmd = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 10);
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const filtersCount = useMemo(() => {
    const macro = selectedFilters.macroCategories?.length || 0;
    const micro = selectedFilters.microCategories?.length || 0;
    const utils =
      currentSearch === 'events' ? selectedFilters.utilities?.length || 0 : 0;
    const price =
      selectedFilters.maxPrice != null &&
      Number.isFinite(Number(selectedFilters.maxPrice))
        ? 1
        : 0;
    // hidePastEvents is on by default; only count it as an active filter
    // when the user has explicitly turned it off (deviation from default).
    const hidePast =
      currentSearch === 'events' && selectedFilters.hidePastEvents === false
        ? 1
        : 0;
    // hideLiveEvents is off by default; only count it when explicitly enabled.
    const hideLive =
      currentSearch === 'events' && selectedFilters.hideLiveEvents === true
        ? 1
        : 0;
    const monthRange =
      currentSearch === 'events' &&
      (selectedFilters.monthRange?.from || selectedFilters.monthRange?.to)
        ? 1
        : 0;
    return macro + micro + utils + price + hidePast + hideLive + monthRange;
  }, [selectedFilters, currentSearch]);

  const resetLocalOnly = () => {
    setSuggestions([]);
    setQuery('');
    setLocation(EMPTY_LOCATION);
    setSelectedFilters({
      macroCategories: [],
      microCategories: [],
      utilities: [],
      maxPrice: null,
      hidePastEvents: true,
      hideLiveEvents: false,
      monthRange: { from: '', to: '' },
    });
    if (currentSearch === 'events') setSelectedDates({ from: '', to: '' });
  };

  // ✅ Close suggestions on outside click
  useEffect(() => {
    const onDown = (e) => {
      const target = e.target;

      if (wrapperRef.current?.contains(target)) return;
      setSuggestions([]);
      setFiltersOpen(false);
    };

    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Debounced search on state change (desktop only)
  useEffect(() => {
    if (redirectToListings) return;
    if (isTabletOrBelow) return;
    if (!didSearchOnceRef.current) return;

    if (skipNextAutoSearchRef.current) {
      skipNextAutoSearchRef.current = false;
      return;
    }

    const tt = setTimeout(() => {
      handleSearch();
    }, 250);

    return () => clearTimeout(tt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    redirectToListings,
    isTabletOrBelow,
    currentSearch,
    location.city,
    location.country,
    location.countryCode,
    location.label,
    selectedDates.from,
    selectedDates.to,
    selectedFilters.macroCategories,
    selectedFilters.microCategories,
    selectedFilters.utilities,
    selectedFilters.maxPrice,
    selectedFilters.hidePastEvents,
    selectedFilters.hideLiveEvents,
    selectedFilters.monthRange?.from,
    selectedFilters.monthRange?.to,
  ]);

  useEffect(() => {
    setLocation(EMPTY_LOCATION);
  }, [currentSearch]);

  useEffect(() => {
    if (!initialState) return;

    // URL params arrive as strings; category ids are numeric everywhere else
    // (catalog, FiltersModal), so coerce + dedupe at this boundary.
    const toIdList = (value) =>
      Array.isArray(value)
        ? [...new Set(value.map(Number).filter(Number.isFinite))]
        : [];

    const macroCategories = toIdList(
      initialState?.selectedFilters?.macroCategories
    );
    const microCategories = toIdList(
      initialState?.selectedFilters?.microCategories
    );
    const utilities = Array.isArray(initialState?.selectedFilters?.utilities)
      ? [...new Set(initialState.selectedFilters.utilities)]
      : [];
    const rawMaxPrice = initialState?.selectedFilters?.maxPrice;
    const parsedMaxPrice = Number(rawMaxPrice);
    const maxPrice =
      rawMaxPrice != null && Number.isFinite(parsedMaxPrice)
        ? parsedMaxPrice
        : null;
    const hidePastEvents =
      initialState?.selectedFilters?.hidePastEvents == null
        ? true
        : Boolean(initialState.selectedFilters.hidePastEvents);
    const hideLiveEvents = Boolean(
      initialState?.selectedFilters?.hideLiveEvents
    );

    const initMonthFrom = isYearMonth(
      initialState?.selectedFilters?.monthRange?.from,
    )
      ? initialState.selectedFilters.monthRange.from
      : '';
    const initMonthTo = isYearMonth(
      initialState?.selectedFilters?.monthRange?.to,
    )
      ? initialState.selectedFilters.monthRange.to
      : '';

    const nextLocation = {
      ...EMPTY_LOCATION,
      ...(initialState.location || {}),
    };
    nextLocation.option = nextLocation.label
      ? {
          label: nextLocation.label,
          value: {
            place_id: nextLocation.placeId || '',
            types: [],
            terms: [],
          },
        }
      : null;

    skipNextAutoSearchRef.current = true;
    setSuggestions([]);
    setQuery(initialState.query || '');
    setLocation(nextLocation);
    setSelectedDates({
      from: initialState?.selectedDates?.from || '',
      to: initialState?.selectedDates?.to || '',
    });
    setSelectedFilters({
      macroCategories,
      microCategories,
      utilities,
      maxPrice,
      hidePastEvents,
      hideLiveEvents,
      monthRange: { from: initMonthFrom, to: initMonthTo },
    });

    didSearchOnceRef.current = Boolean(initialState.hasActiveSearch);
  }, [currentSearch, initialState, initialStateKey]);

  // ✅ Debounced suggestions
  useEffect(() => {
    if (justSelectedSuggestion.current) {
      justSelectedSuggestion.current = false;
      return;
    }

    const q = (query || '').trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }

    let alive = true;
    const timer = setTimeout(() => {
      if (!alive) return;

      if (currentSearch === 'brands') {
        dispatch(fetchSuggestions(q, 'brands'))
          .then((backendSuggestions) => {
            if (!alive) return;
            const categorySuggestions = getCategorySuggestions(q);

            const seen = new Set();
            const merged = [
              ...categorySuggestions,
              ...backendSuggestions,
            ].filter((s) => {
              const k = `${s.type || 'x'}:${(s.name || '').toLowerCase()}`;
              if (seen.has(k)) return false;
              seen.add(k);
              return true;
            });

            setSuggestions(merged);
          })
          .catch(() => alive && setSuggestions([]));
      } else {
        setSuggestions(getCategorySuggestions(q));
      }
    }, 200);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query, currentSearch, dispatch]);

  // ✅ Events: allow ONLY city or country suggestions
  const isCityOrCountry = (types = []) => {
    if (!Array.isArray(types)) return false;

    return (
      types.includes('country') ||
      types.includes('locality') ||
      types.includes('postal_town') ||
      types.includes('administrative_area_level_3') ||
      types.includes('administrative_area_level_2')
    );
  };

  const isCountryPrediction = (opt) => {
    const types = opt?.data?.value?.types || [];
    const terms = opt?.data?.value?.terms || [];
    const isCountry = Array.isArray(types) && types.includes('country');
    const looksLikeCountry = Array.isArray(terms) && terms.length === 1;
    return isCountry || looksLikeCountry;
  };

  // Styles for Google Places Autocomplete (react-select)
  const gpaStyles = useMemo(
    () => ({
      container: (provided) => ({
        ...provided,
        width: '100%',
        borderRadius: '9999px',
        border: '1px solid',
        borderColor: '#E5E7EB',
        boxShadow: 'none',
        outline: 'none',
      }),
      control: (p) => ({
        ...p,
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        cursor: 'text',
        minHeight: isMobile ? '44px' : '44px',
        height: isMobile ? '44px' : '44px',
        paddingLeft: isMobile ? '34px' : '40px',
        borderRadius: '9999px',
        fontSize: isMobile ? '16px' : '14px', // ✅ helps iOS
      }),
      valueContainer: (p) => ({
        ...p,
        height: isMobile ? '44px' : '44px',
        padding: 0,
      }),
      input: (p) => ({
        ...p,
        margin: 0,
        padding: 0,
        color: 'black',
        fontSize: isMobile ? '16px' : '14px', // ✅
      }),
      singleValue: (p) => ({
        ...p,
        color: 'black',
        fontSize: isMobile ? '16px' : '14px', // ✅
      }),
      placeholder: (p) => ({
        ...p,
        color: '#9BA3AF',
        fontSize: isMobile ? '16px' : '14px', // ✅
      }),
      dropdownIndicator: (p) => ({
        ...p,
        color: 'white',
        padding: isMobile ? '2px' : '4px',
        '&:hover': { color: '#9BA3AF' },
      }),
      indicatorSeparator: () => ({ display: 'none' }),
      // The menu renders inline (not portaled) so it stays glued to the
      // input when the page scrolls — a fixed/portaled menu keeps its
      // open-time coordinates and drifts away from the field.
      menu: (p) => ({
        ...p,
        borderRadius: '8px',
        marginTop: '4px',
        boxShadow:
          '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        width: '100%',
        zIndex: 50,
      }),
      menuList: (p) => ({
        ...p,
        paddingTop: '6px',
        paddingBottom: '6px',
        maxHeight: isMobile ? '240px' : '300px',
        fontSize: isMobile ? '16px' : '14px', // optional
      }),
      option: (p, state) => ({
        ...p,
        paddingTop: isMobile ? '8px' : '10px',
        paddingBottom: isMobile ? '8px' : '10px',
        paddingLeft: isMobile ? '10px' : '12px',
        paddingRight: isMobile ? '10px' : '12px',
        fontSize: isMobile ? '16px' : '14px', // optional
        backgroundColor: state.isFocused ? '#F3F4F6' : 'white',
        color: '#111827',
      }),
    }),
    [isMobile]
  );

  const appendQueryValue = (queryParams, key, value) => {
    if (Array.isArray(value)) {
      const list = value
        .map((item) => String(item || '').trim())
        .filter(Boolean);
      if (list.length) queryParams.set(key, list.join(','));
      return;
    }

    if (value === undefined || value === null) return;
    const normalized = String(value).trim();
    if (!normalized) return;
    queryParams.set(key, normalized);
  };

  const redirectWithFilters = (filters) => {
    const queryParams = new URLSearchParams();
    queryParams.set('searchType', currentSearch || 'events');

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (
        key === 'maxPrice' &&
        (value == null || !Number.isFinite(Number(value)))
      )
        return;
      if (key === 'hidePastEvents') {
        // Default is true; only emit when the user explicitly disabled it.
        if (value === false) queryParams.set(key, 'false');
        return;
      }
      if (key === 'hideLiveEvents') {
        // Default is false; only emit when the user explicitly enabled it.
        if (value === true) queryParams.set(key, 'true');
        return;
      }
      appendQueryValue(queryParams, key, value);
    });

    navigate(`${listingsPath}?${queryParams.toString()}`);
  };

  const handleSearch = (customFilters, customQuery) => {
    didSearchOnceRef.current = true;

    const q = (
      typeof customQuery === 'string' ? customQuery : query || ''
    ).trim();

    if (currentSearch === 'events') {
      const { qMacroIds, qMicroIds, qUtilityIds } = getIdsFromQuery(q);

      const merged = {
        macroCategories: selectedFilters.macroCategories || [],
        microCategories: selectedFilters.microCategories || [],
        utilities: selectedFilters.utilities || [],
        maxPrice: selectedFilters.maxPrice,
        hidePastEvents: Boolean(selectedFilters.hidePastEvents),
        hideLiveEvents: Boolean(selectedFilters.hideLiveEvents),
        monthRange: selectedFilters.monthRange || { from: '', to: '' },
        ...(customFilters || {}),
      };

      // Merge query-matched utility IDs into the utilities filter
      const mergedUtilities = [
        ...new Set([...(merged.utilities || []), ...qUtilityIds]),
      ];

      const onlyUtilityMatch =
        qUtilityIds.length > 0 &&
        qMacroIds.length === 0 &&
        qMicroIds.length === 0;
      const effectiveQ = onlyUtilityMatch ? '' : q;

      // The day-precision "When" picker keeps its overlap semantics
      // (startDate/endDate). The month/year range is an independent filter
      // that the backend applies against the event's *start* date.
      const monthFrom = isYearMonth(merged.monthRange?.from)
        ? merged.monthRange.from
        : '';
      const monthTo = isYearMonth(merged.monthRange?.to)
        ? merged.monthRange.to
        : '';

      const eventFilters = {
        q: effectiveQ,
        qMacroCategories: qMacroIds.join(','),
        qMicroCategories: qMicroIds.join(','),

        // structured location params (preferred)
        city: location.city || '',
        country: location.country || '',
        countryCode: location.countryCode || '',

        // legacy params (fallback)
        location: location.primary || '',
        locationLabel: location.label || '',

        startDate: toYmd(selectedDates.from),
        endDate: toYmd(selectedDates.to),

        // Month/year range (event start date must fall within this window).
        monthFrom,
        monthTo,

        macroCategories: merged.macroCategories,
        microCategories: merged.microCategories,
        utilities: mergedUtilities,
        maxPrice: merged.maxPrice,
        hidePastEvents: Boolean(merged.hidePastEvents),
        hideLiveEvents: Boolean(merged.hideLiveEvents),
      };

      // Pages that render their own results off `state.events` (the map) need
      // to know which time filters produced them, so they can mirror the
      // listing's client-side hiding instead of showing everything the
      // request happened to return.
      onEventFiltersChange?.({
        hidePastEvents: eventFilters.hidePastEvents,
        hideLiveEvents: eventFilters.hideLiveEvents,
      });

      if (redirectToListings) {
        redirectWithFilters(eventFilters);
      } else {
        dispatch(searchEvents(eventFilters));
      }
      return;
    }

    if (currentSearch === 'brands') {
      const { qMacroIds, qMicroIds } = getIdsFromQuery(q);

      const merged = {
        macroCategories: selectedFilters.macroCategories || [],
        microCategories: selectedFilters.microCategories || [],
        ...(customFilters || {}),
      };

      const brandFilters = {
        q,
        qMacroCategories: qMacroIds,
        qMicroCategories: qMicroIds,

        macroCategories: merged.macroCategories,
        microCategories: merged.microCategories,

        countryCode: location.countryCode || '',
        city: '',
        country: location.country || '',
        locationLabel: location.label || '',
      };

      if (redirectToListings) {
        redirectWithFilters(brandFilters);
      } else {
        dispatch(searchBrands(brandFilters, { lang: i18n.language }));
      }
    }
  };

  const handleApplyFilters = (filters) => {
    // Apply immediately — the user shouldn't have to click the magnifying
    // glass after choosing filters. We pass `filters` straight to
    // handleSearch (via customFilters) because setSelectedFilters is async
    // and wouldn't be reflected in this synchronous call yet. The skip flag
    // prevents the debounced auto-search (live mode) from firing a duplicate.
    skipNextAutoSearchRef.current = true;
    setSelectedFilters((prev) => ({ ...prev, ...filters }));
    setIsFiltersModalOpen(false);
    handleSearch(filters);
  };

  const handleClearFilters = () => {
    setIsFiltersModalOpen(false);

    // Reset every piece of local state (query, location, dates, filters).
    skipNextAutoSearchRef.current = true;
    resetLocalOnly();

    // Don't route the cleared search through handleSearch: it reads
    // `query`, `location` and `selectedDates` from the current render's
    // closure, which still hold the *old* values at this point (state
    // updates are async). That stale data kept leaking back into the
    // request, so some events appeared to "disappear" after clearing.
    // Instead, go straight to a pristine listing / live search.
    if (redirectToListings) {
      const queryParams = new URLSearchParams();
      queryParams.set('searchType', currentSearch || 'events');
      navigate(`${listingsPath}?${queryParams.toString()}`);
      return;
    }

    if (currentSearch === 'events') {
      onEventFiltersChange?.({ hidePastEvents: true, hideLiveEvents: false });
      dispatch(searchEvents({ hidePastEvents: true, lang: i18n.language }));
    } else {
      dispatch(searchBrands({}, { lang: i18n.language }));
    }
  };

  const SuggestionsDropdown = () => {
    if (suggestions.length === 0) return null;

    const BrandAvatar = ({ sug }) => {
      const url = sug.avatar || sug.logo || sug.image || sug.imageUrl;
      if (url) {
        return (
          <img
            src={url}
            alt=""
            className="w-6 h-6 rounded-full object-cover mr-2 flex-shrink-0"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        );
      }
      return (
        <BuildingStorefrontIcon className="w-5 h-5 mr-2 text-gray-500 flex-shrink-0" />
      );
    };

    const LeftIcon = ({ sug }) => {
      if (sug.type === 'category') {
        return (
          <CategoryIcon
            macro={sug.macroId}
            className="w-5 h-5 mr-2 flex-shrink-0"
          />
        );
      }
      if (sug.type === 'subcategory') {
        return (
          <CategoryIcon
            macro={sug.macroId}
            microCat={sug.microId}
            className="w-5 h-5 mr-2 flex-shrink-0"
          />
        );
      }
      if (sug.type === 'utility') {
        return (
          <AdjustmentsHorizontalIcon className="w-5 h-5 mr-2 text-yellow-500 flex-shrink-0" />
        );
      }
      return <BrandAvatar sug={sug} />;
    };

    const RightPill = ({ sug }) => (
      <span className="ml-3 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 flex-shrink-0">
        {sug.type === 'category'
          ? t('listing.category')
          : sug.type === 'subcategory'
            ? t('listing.subcategory')
            : sug.type === 'utility'
              ? t('listing.utilities')
              : t('common.brand')}
      </span>
    );

    return (
      <div className="absolute left-0 right-0 sm:right-auto sm:w-[28rem] mt-2 z-50 bg-white rounded-2xl ring-1 ring-line shadow-lift max-h-80 overflow-y-auto">
        {suggestions.map((sug, index) => (
          <div
            key={sug.id || `${sug.type}-${sug.name}-${index}`}
            onClick={() => {
              justSelectedSuggestion.current = true;
              setQuery(sug.name);
              setSuggestions([]);

              // desktop: search immediately. tablet/modal: wait for "Search" button.
              if (!isTabletOrBelow && !redirectToListings) {
                handleSearch(undefined, sug.name);
              }
            }}
            className="flex items-center justify-between px-4 py-3 hover:bg-gray-100 cursor-pointer"
          >
            <div className="flex items-center min-w-0">
              <LeftIcon sug={sug} />
              <span className="text-gray-800 text-sm truncate">{sug.name}</span>
            </div>
            <RightPill sug={sug} />
          </div>
        ))}
      </div>
    );
  };

  const onQueryKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSuggestions([]);
      handleSearch();
    }
  };

  const onPlaceChange = async (place) => {
    if (!redirectToListings) didSearchOnceRef.current = true;

    if (!place) {
      setLocation(EMPTY_LOCATION);
      return;
    }

    const predTypes = place?.value?.types || [];
    const predTerms = place?.value?.terms || [];
    const looksLikeCountry = Array.isArray(predTerms) && predTerms.length === 1;
    const isCountryPred =
      (Array.isArray(predTypes) && predTypes.includes('country')) ||
      looksLikeCountry;

    // EVENTS: only allow city or country
    if (currentSearch === 'events' && !isCityOrCountry(predTypes)) {
      setLocation(EMPTY_LOCATION);
      return;
    }

    // BRANDS: allow only country
    if (currentSearch === 'brands' && !isCountryPred) {
      setLocation(EMPTY_LOCATION);
      return;
    }

    const label = String(place.label || '').trim();
    const placeId = place?.value?.place_id || '';

    const parts = label
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);

    const primary = parts[0] || label;

    let initialCity = '';
    let initialCountry = '';

    if (isCountryPred && parts.length === 1) {
      initialCountry = primary;
    } else if (parts.length >= 2) {
      initialCity = parts[0];
      initialCountry = parts[parts.length - 1];
    } else {
      initialCity = primary;
    }

    setLocation({
      option: place,
      label,
      primary,
      city: currentSearch === 'brands' ? '' : initialCity,
      country:
        currentSearch === 'brands' ? primary || initialCountry : initialCountry,
      countryCode: '',
      placeId,
    });

    if (!placeId) return;

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();

      const result = data?.results?.[0];
      const comps = result?.address_components || [];
      const geoTypes = result?.types || [];

      const countryComp = comps.find((c) => c.types.includes('country'));
      const geoCountry = countryComp?.long_name || initialCountry || '';
      const geoCountryCode = countryComp?.short_name || '';

      const cityComp =
        comps.find((c) => c.types.includes('locality')) ||
        comps.find((c) => c.types.includes('postal_town')) ||
        comps.find((c) => c.types.includes('administrative_area_level_3')) ||
        comps.find((c) => c.types.includes('administrative_area_level_2'));

      const geoCity = cityComp?.long_name || initialCity || '';

      const isCountryResult =
        geoTypes.includes('country') ||
        (comps.length === 1 && comps[0]?.types?.includes('country'));

      if (currentSearch === 'brands' && !isCountryResult) {
        setLocation(EMPTY_LOCATION);
        return;
      }

      if (currentSearch === 'brands') {
        setLocation((prev) => ({
          ...prev,
          city: '',
          country: geoCountry || prev.country,
          countryCode: geoCountryCode || prev.countryCode,
        }));
        return;
      }

      // Events: city OR country
      if (isCountryResult) {
        setLocation((prev) => ({
          ...prev,
          city: '',
          country: geoCountry || prev.country,
          countryCode: geoCountryCode || prev.countryCode,
        }));
      } else {
        setLocation((prev) => ({
          ...prev,
          city: geoCity || prev.city,
          country: geoCountry || prev.country,
          countryCode: geoCountryCode || prev.countryCode,
        }));
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
  };


  // Rotating example prompts (T2B-390) — same lists the homepage hero uses.
  // On mobile the overlay turns into a scrolling marquee; on larger screens
  // it stays a static placeholder.
  const placeholderList = t(
    currentSearch === 'brands'
      ? 'homepage.search_brands_placeholders'
      : 'homepage.search_events_placeholders',
    { returnObjects: true }
  );
  const basePlaceholder =
    currentSearch === 'brands'
      ? t('homepage.search_brands_placeholder')
      : t('homepage.search_events_placeholder');

  // Pill styled to match the homepage HeroSearch (all breakpoints)
  return (
    <div className="relative w-full" ref={wrapperRef}>
      {hasLabel && (
        <div
          className={`w-full max-w-3xl ${isHomepage ? '' : 'mx-auto'} px-3 mb-1 text-gray-400 text-sm font-medium tracking-wide select-none pointer-events-none text-left`}
        >
          {currentSearch === 'events'
            ? t('listing.search_events')
            : t('listing.search_brands')}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSuggestions([]);
          setFiltersOpen(false);
          handleSearch();
        }}
        className={`relative w-full max-w-3xl ${isHomepage ? '' : 'mx-auto'}`}
      >
        <div className="bg-white rounded-full ring-1 ring-line shadow-card p-1.5 flex items-center gap-1 transition">
          {/* What / keyword */}
          <div className="flex-1 flex items-center gap-2 px-4 min-w-0 relative">
            <MagnifyingGlassIcon className="h-[15px] w-[15px] text-gray-400 shrink-0" />
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onQueryKeyDown}
                placeholder=""
                aria-label={basePlaceholder}
                className="w-full bg-transparent text-[16px] sm:text-[14px] text-ink-strong focus:outline-none min-w-0 py-2"
              />
              {!query && (
                <ScrollingPlaceholder
                  text={basePlaceholder}
                  items={Array.isArray(placeholderList) ? placeholderList : []}
                />
              )}
            </div>
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setSuggestions([]);
                }}
                className="shrink-0 p-1 rounded-full hover:bg-gray-100"
                aria-label={t('listing.clear')}
              >
                <XMarkIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>

          {/* Filters toggle */}
          <button
            type="button"
            onClick={() => {
              setSuggestions([]);
              setFiltersOpen((v) => !v);
            }}
            className={`relative shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full text-[13px] font-bold transition ring-1 ${
              filtersOpen || filtersCount > 0
                ? 'bg-ink-strong text-white ring-ink-strong'
                : 'bg-white text-ink-strong ring-line hover:ring-ink-strong'
            }`}
            aria-label={t('listing.title')}
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{t('listing.title')}</span>
            {filtersCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-brand text-ink-strong text-[11px] font-extrabold">
                {filtersCount}
              </span>
            )}
          </button>

          <button
            type="submit"
            aria-label={t('listing.search')}
            className={`shrink-0 p-2.5 sm:px-5 sm:py-2.5 rounded-full text-[13px] font-extrabold transition shadow-sm ${
              currentSearch === 'brands'
                ? 'bg-ink-strong text-white hover:bg-black'
                : 'bg-brand text-ink-strong hover:bg-brand-hover'
            }`}
          >
            <MagnifyingGlassIcon className="h-5 w-5 sm:hidden" />
            <span className="hidden sm:inline">{t('listing.search')}</span>
          </button>
        </div>

        <SuggestionsDropdown />

        {/* Filters panel — Where & When live here (like the homepage) */}
        {filtersOpen && (
          <div className="absolute left-0 right-0 mt-2 z-40 bg-white rounded-2xl ring-1 ring-line shadow-lift p-4 space-y-3">
            {/* Where */}
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-1.5">
                {t('listing.where')}
              </div>
              <div className="relative rounded-full ring-1 ring-line bg-white">
                <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                  <MapPinIcon className="h-5 w-5" />
                </span>
                <GooglePlacesAutocomplete
                  key={`where-${currentSearch}-panel`}
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  apiOptions={{
                    language: (i18n.language || 'en').split('-')[0],
                  }}
                  autocompletionRequest={{ types: ['(regions)'] }}
                  selectProps={{
                    value: location.option,
                    onChange: onPlaceChange,
                    placeholder: t('listing.where'),
                    noOptionsMessage: () => t('homepage.no_options'),
                    styles: gpaStyles,
                    menuPlacement: 'bottom',
                    menuShouldScrollIntoView: false,
                    filterOption: (opt) => {
                      const types = opt?.data?.value?.types || [];
                      return currentSearch === 'events'
                        ? isCityOrCountry(types)
                        : isCountryPrediction(opt);
                    },
                  }}
                />
                {location.label && (
                  <button
                    type="button"
                    onClick={() => {
                      didSearchOnceRef.current = true;
                      setLocation(EMPTY_LOCATION);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition"
                    aria-label="Clear location"
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            {/* When (events only) */}
            {currentSearch === 'events' && (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-1.5">
                  {t('listing.when')}
                </div>
                <DatePicker
                  selectedDates={selectedDates}
                  onDateChange={setSelectedDates}
                  placeholder={t('listing.when')}
                />
              </div>
            )}

            {/* More filters + actions */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsFiltersModalOpen(true)}
                  className="inline-flex items-center gap-1.5 text-[13px] font-bold text-ink-strong hover:underline"
                >
                  <AdjustmentsHorizontalIcon className="h-4 w-4" />
                  {t('homepage.more_filters')}
                  {filtersCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-brand text-ink-strong text-[11px] font-extrabold">
                      {filtersCount}
                    </span>
                  )}
                </button>
                {filtersCount > 0 && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="text-[13px] font-semibold text-gray-400 hover:text-ink-strong"
                  >
                    {t('listing.clear')}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setFiltersOpen(false);
                  setSuggestions([]);
                  handleSearch();
                }}
                className="px-4 py-2 rounded-full bg-ink-strong text-white text-[13px] font-bold hover:bg-black transition"
              >
                {t('listing.search')}
              </button>
            </div>
          </div>
        )}
      </form>

      {/* Desktop filters modal only */}
      <FiltersModal
        isBrandSearch={currentSearch === 'brands'}
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        categories={categories || []}
        utilities={currentSearch === 'events' ? UTILITIES : []}
        initialFilters={selectedFilters}
        isAuthenticated={isAuthenticated}
      />
    </div>
  );
};

export default EventSearchBar;
